/**
 * Landing — public marketing one-pager.
 *
 * Shown at `/` when the visitor is NOT logged in. Replaces the previous
 * behavior of immediately rendering the SignIn screen for everyone.
 *
 * Required by Apple App Store + Google Play submission so the "Marketing URL"
 * field points to something user-friendly. Also serves as the open-graph
 * preview for universal share links and invite links.
 *
 * Styling matches Kinly's design system. Mobile-first. Pure CSS, no images
 * beyond the logo (icons live in public/).
 */
import React from "react";
import { Link } from "react-router-dom";
import { CalendarDays, MessageCircle, Camera, Sparkles, ShieldCheck, Heart } from "lucide-react";
import { MarketingNav, MarketingFooter } from "@/components/marketing/MarketingChrome";

export default function Landing() {
  return (
    <div className="min-h-dvh bg-background text-foreground">
      <MarketingNav active="home" />

      {/* Hero */}
      <section className="px-5 sm:px-8 pt-12 sm:pt-20 pb-16 sm:pb-24">
        <div className="max-w-[1080px] mx-auto">
          <div className="max-w-[760px]">
            <p className="text-[11px] uppercase tracking-[0.22em] text-primary font-medium mb-4">
              Your family co-pilot
            </p>
            <h1 className="font-display text-4xl sm:text-6xl lg:text-7xl font-medium leading-[1.05] tracking-tight">
              Run your family<br/>on one screen.
            </h1>
            <p className="mt-6 sm:mt-8 text-[17px] sm:text-[19px] text-muted-foreground leading-relaxed max-w-[640px]">
              Designed for families that need to run efficiently. Kinly brings your calendar,
              chores, photos, and chat together — and remembers what matters so you don't have to.
              No ads. No data sold. Ever.
            </p>
            <div className="mt-8 sm:mt-10 flex flex-col sm:flex-row gap-3 sm:gap-4">
              <a
                href="#download"
                data-testid="landing-cta-download"
                className="h-12 px-7 rounded-full bg-foreground text-background text-[15px] font-medium tap inline-flex items-center justify-center gap-2 hover:opacity-90 transition"
              >
                Download Kinly
              </a>
              <Link
                to="/choose-plan"
                data-testid="landing-cta-signup"
                className="h-12 px-7 rounded-full border border-border text-foreground text-[15px] font-medium tap inline-flex items-center justify-center hover:bg-muted/50 transition"
              >
                Create a free account
              </Link>
            </div>
            <p className="mt-4 text-xs text-muted-foreground">
              30-day free trial · cancel anytime · no card needed to sign up
            </p>
          </div>
        </div>
      </section>

      {/* Feature grid */}
      <section className="px-5 sm:px-8 py-14 sm:py-20 bg-muted/30 border-y border-border/40">
        <div className="max-w-[1080px] mx-auto">
          <h2 className="font-display text-2xl sm:text-3xl font-medium tracking-tight mb-2">
            What Kinly handles.
          </h2>
          <p className="text-muted-foreground text-[15px] max-w-[640px] mb-10 sm:mb-14">
            Built so a busy family can move faster, together.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5">
            <FeatureCard
              icon={CalendarDays}
              title="Voice → calendar"
              body='Tap, speak, done. Kinly hears "Mia has a dentist on Friday at 4pm" and adds it — with the right people, location, and a reminder.'
            />
            <FeatureCard
              icon={Heart}
              title="Recipes that build the shopping list"
              body="Snap a recipe or paste a link. One tap sends every ingredient to the right shopping list."
            />
            <FeatureCard
              icon={MessageCircle}
              title="A chat that remembers"
              body="Mia mentions she loves strawberries. Kinly notices and remembers, so the next recipe suggestion fits."
            />
            <FeatureCard
              icon={Camera}
              title="Photos that find themselves"
              body="Upload from any phone. Kinly groups them by face and place — find that beach day in seconds."
            />
            <FeatureCard
              icon={ShieldCheck}
              title="You stay in control"
              body="See exactly what Kinly knows. Turn off anything you'd rather it didn't see. Right-to-be-forgotten is built in."
            />
            <FeatureCard
              icon={Sparkles}
              title="One family memory"
              body="Calendar, chores, chat, photos — all reading from the same family memory. So Kinly actually knows what's going on."
            />
          </div>
        </div>
      </section>

      {/* Pricing teaser — full ladder lives at /pricing */}
      <section className="px-5 sm:px-8 py-14 sm:py-20">
        <div className="max-w-[1080px] mx-auto text-center">
          <h2 className="font-display text-2xl sm:text-3xl font-medium tracking-tight mb-2">
            Fair pricing. Family pricing.
          </h2>
          <p className="text-muted-foreground text-[15px] max-w-[640px] mx-auto mb-8">
            Five tiers, every paid plan ships with a free first month. Start free with your partner — upgrade only when you're ready.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link
              to="/pricing"
              data-testid="landing-cta-pricing"
              className="h-12 px-7 rounded-full bg-foreground text-background text-[15px] font-medium tap inline-flex items-center justify-center"
            >
              See all plans
            </Link>
            <Link
              to="/features"
              data-testid="landing-cta-features"
              className="h-12 px-7 rounded-full border border-border text-foreground text-[15px] font-medium tap inline-flex items-center justify-center"
            >
              Explore features
            </Link>
          </div>
        </div>
      </section>

      {/* Download */}
      <section id="download" className="px-5 sm:px-8 py-14 sm:py-20 bg-foreground text-background">
        <div className="max-w-[760px] mx-auto text-center">
          <h2 className="font-display text-2xl sm:text-4xl font-medium tracking-tight">
            Get Kinly.
          </h2>
          <p className="mt-4 text-[15px] sm:text-[17px] text-background/70 leading-relaxed">
            Coming to iOS and Android. Drop your email and we'll send you the link the moment it's live.
          </p>
          <form
            className="mt-8 flex flex-col sm:flex-row gap-2 max-w-[520px] mx-auto"
            onSubmit={(e) => {
              e.preventDefault();
              const email = new FormData(e.currentTarget).get("email");
              window.location.href = `mailto:hello@kinly.geminii-ai.com?subject=Notify%20me%20when%20Kinly%20launches&body=${encodeURIComponent(`Please notify me at ${email}.`)}`;
            }}
          >
            <input
              data-testid="landing-waitlist-email"
              name="email"
              type="email"
              required
              placeholder="you@home.com"
              className="flex-1 h-12 rounded-full px-5 bg-background/10 border border-background/20 text-background placeholder:text-background/40 outline-none focus:border-background/40"
            />
            <button
              data-testid="landing-waitlist-submit"
              type="submit"
              className="h-12 px-6 rounded-full bg-background text-foreground text-[14px] font-medium tap"
            >
              Notify me
            </button>
          </form>
          <p className="mt-8 text-[11px] uppercase tracking-[0.22em] text-background/40">
            Coming to the App Store · Google Play · Q3 2026
          </p>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
}

function FeatureCard({ icon: Icon, title, body }) {
  return (
    <article
      data-testid={`landing-feature-${title.toLowerCase().slice(0, 12).replace(/\s+/g, "-")}`}
      className="kinly-card hover:border-primary/30 transition-colors"
    >
      <div className="w-10 h-10 rounded-full bg-primary/15 text-primary flex items-center justify-center mb-3">
        <Icon className="w-5 h-5" strokeWidth={1.7} />
      </div>
      <h3 className="font-display text-[17px] font-medium leading-tight mb-1.5 tracking-tight">{title}</h3>
      <p className="text-[14px] text-muted-foreground leading-relaxed">{body}</p>
    </article>
  );
}
